import { extractLevel } from "../extract/walk";
import { ensureHuman, ensureToken, proxyToken } from "./guard";
import { DEMO_URL, MAP_FONT_CSS, MAX_GLYPHS } from "../shared/defs";
import { RISK_MESSAGE, riskyTarget } from "../shared/risk";
import type { ExtractOpts, ImgPaint, Level } from "../shared/types";

export interface LayoutBox {
  layoutW: number;
  viewH: number;
  maxH: number;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const DEMO_SNAPSHOT = "/demo/archive-org/index.html";

/** Empty field, the word demo, and the archive.org homepage use the bundled snapshot. */
export function isBundledDemo(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed || /^demo$/i.test(trimmed)) return true;
  const abs = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(abs);
    const host = url.hostname.toLowerCase().replace(/\.$/, "");
    if (host !== "archive.org" && host !== "www.archive.org") return false;
    if (url.search || url.hash) return false;
    return url.pathname.replace(/\/+$/, "") === "";
  } catch {
    return false;
  }
}

export function classify(raw: string): { kind: "demo" | "map" | "web"; url: string; label: string } {
  const trimmed = raw.trim();
  if (isBundledDemo(trimmed)) return { kind: "demo", url: DEMO_URL, label: "archive.org" };
  if (trimmed.startsWith("/assets/maps/")) {
    const name = trimmed.split("/")[3] || "Map";
    return { kind: "map", url: trimmed, label: name };
  }
  const abs = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    return { kind: "web", url: abs, label: new URL(abs).host };
  } catch {
    return { kind: "web", url: abs, label: trimmed };
  }
}

function options(box: LayoutBox, pageUrl: string): ExtractOpts {
  return {
    layoutW: box.layoutW,
    viewH: box.viewH,
    maxH: box.maxH,
    maxGlyphs: MAX_GLYPHS,
    origin: location.origin,
    pageUrl,
  };
}

export async function decodeLevel(buf: ArrayBuffer): Promise<Level> {
  const bytes = new Uint8Array(buf);
  let text: string;
  if (bytes[0] === 0x1f && bytes[1] === 0x8b && typeof DecompressionStream !== "undefined") {
    const stream = new Blob([buf]).stream().pipeThrough(new DecompressionStream("gzip"));
    text = await new Response(stream).text();
  } else text = new TextDecoder().decode(buf);
  return JSON.parse(text) as Level;
}

export async function fetchLevel(url: string): Promise<Level> {
  const res = await fetch(url);
  if (!res.ok) throw new Error("That map is missing.");
  return decodeLevel(await res.arrayBuffer());
}

const PORTAL_COLLAPSED = "The portal collapsed.";

/** Journey hops: collapse after this long with no loading progress. */
const HOP_STALL_MS = 8000;
/** Journey hops: collapse even if the bar is still moving. */
const HOP_CAP_MS = 25000;

/**
 * Progress watchdog for a portal hop. The stall timer resets when the load
 * actually advances. A hard cap still ends a hop that keeps creeping.
 * `rearm` opens one more stall window after a strategy that never really started.
 */
export interface HopWatch {
  note(p: number, msg?: string): void;
  signal(): AbortSignal;
  peak(): number;
  failed(): boolean;
  capped(): boolean;
  rearm(): boolean;
  stop(): void;
}

export function createHopWatch(stallMs = HOP_STALL_MS, capMs = HOP_CAP_MS): HopWatch {
  const started = performance.now();
  let ctrl = new AbortController();
  let timer = 0;
  let dead = false;
  let peak = 0;
  let lastP = -1;
  let lastMsg = "";

  const kill = () => {
    if (dead) return;
    dead = true;
    window.clearTimeout(timer);
    timer = 0;
    if (!ctrl.signal.aborted) ctrl.abort();
  };
  const arm = () => {
    window.clearTimeout(timer);
    if (dead) return;
    const capLeft = capMs - (performance.now() - started);
    if (capLeft <= 0) {
      kill();
      return;
    }
    timer = window.setTimeout(kill, Math.max(1, Math.min(stallMs, capLeft)));
  };
  arm();

  return {
    note(p, msg = "") {
      if (dead) return;
      if (p > peak) peak = p;
      const moved = p > lastP + 1e-4 || (msg.length > 0 && msg !== lastMsg);
      if (!moved) return;
      lastP = Math.max(lastP, p);
      if (msg) lastMsg = msg;
      if (performance.now() - started >= capMs) {
        kill();
        return;
      }
      arm();
    },
    signal: () => ctrl.signal,
    peak: () => peak,
    failed: () => dead,
    capped: () => performance.now() - started >= capMs,
    rearm() {
      if (performance.now() - started >= capMs) {
        kill();
        return false;
      }
      dead = false;
      ctrl = new AbortController();
      arm();
      return true;
    },
    stop() {
      window.clearTimeout(timer);
      timer = 0;
    },
  };
}

function aborted(err: unknown): boolean {
  return !!err && typeof err === "object" && "name" in err && (err as { name?: string }).name === "AbortError";
}

/** Network blocks, aborts, and proxy refusals collapse a hop without another long wait. */
function blockedRequest(err: unknown): boolean {
  if (aborted(err) || err instanceof TypeError) return true;
  const message = err instanceof Error ? err.message : "";
  return /could not be reached|bad address|answered [45]\d\d|can't be opened/i.test(message);
}

function whenCollapsed(signal: AbortSignal): { promise: Promise<never>; cancel: () => void } {
  let onAbort = () => {};
  const promise = new Promise<never>((_, reject) => {
    onAbort = () => reject(new Error(PORTAL_COLLAPSED));
    if (signal.aborted) {
      reject(new Error(PORTAL_COLLAPSED));
      return;
    }
    signal.addEventListener("abort", onAbort, { once: true });
  });
  promise.catch(() => undefined);
  return { promise, cancel: () => signal.removeEventListener("abort", onAbort) };
}

async function readHtml(res: Response, progress: (msg: string, p: number) => void, watch?: HopWatch): Promise<string> {
  if (!watch || !res.body) return res.text();
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  const total = Number(res.headers.get("content-length")) || 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value?.byteLength) continue;
      chunks.push(value);
      received += value.byteLength;
      const frac = total > 0 ? Math.min(1, received / total) : Math.min(1, received / 250000);
      progress("Fetching the page", 0.05 + frac * 0.09);
    }
  } catch (err) {
    if (watch.failed() || aborted(err) || err instanceof TypeError) throw new Error(PORTAL_COLLAPSED);
    throw err;
  }
  const buf = new Uint8Array(received);
  let offset = 0;
  for (const chunk of chunks) {
    buf.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return new TextDecoder().decode(buf);
}

async function staticPage(raw: string, box: LayoutBox, progress: (msg: string, p: number) => void, watch?: HopWatch): Promise<Level> {
  progress("Fetching the page", 0.05);
  const signal = watch?.signal();
  const res = await fetch(`/api/page?url=${encodeURIComponent(raw)}`, signal ? { signal } : undefined).catch((err: unknown) => {
    if (watch && (aborted(err) || err instanceof TypeError || watch.failed())) throw new Error(PORTAL_COLLAPSED);
    throw err;
  });
  const type = res.headers.get("content-type") || "";
  if (!res.ok || type.includes("application/json")) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || "The site could not be reached.");
  }
  const html = await readHtml(res, progress, watch);
  const finalUrl = res.headers.get("x-final-url") || raw;
  progress("Laying it out", 0.15);
  progress("Loading fonts and images", 0.25);
  const iframe = document.createElement("iframe");
  iframe.setAttribute("sandbox", "allow-same-origin");
  iframe.title = "page";
  iframe.style.cssText = `position:fixed;left:0;top:0;width:${box.layoutW}px;height:${Math.max(640, box.viewH)}px;opacity:0;pointer-events:none;border:0;z-index:-1`;
  const loaded = new Promise<void>((resolve, reject) => {
    const timer = watch ? 0 : window.setTimeout(() => reject(new Error("The page took too long.")), 20000);
    const stop = () => window.clearTimeout(timer);
    iframe.onload = () => {
      stop();
      resolve();
    };
    if (watch) {
      const onAbort = () => {
        stop();
        reject(new Error(PORTAL_COLLAPSED));
      };
      if (watch.failed()) onAbort();
      else watch.signal().addEventListener("abort", onAbort, { once: true });
    }
  });
  iframe.srcdoc = html;
  document.body.appendChild(iframe);
  const gate = watch ? whenCollapsed(watch.signal()) : null;
  try {
    await loaded;
    if (watch?.failed()) throw new Error(PORTAL_COLLAPSED);
    const doc = iframe.contentDocument;
    const view = iframe.contentWindow;
    if (!doc || !view) throw new Error("Could not read the page.");
    const pending = extractLevel(doc, view, options(box, finalUrl), (msg, p) => progress(msg, 0.25 + p * 0.7));
    pending.catch(() => undefined);
    const level = gate ? await Promise.race([pending, gate.promise]) : await pending;
    const items = level.paints.length + level.glyphs.length + level.blocks.length;
    if (raw !== "demo" && items < 8) throw new Error("The page had nothing to break.");
    return level;
  } catch (err) {
    if (watch && (watch.failed() || aborted(err))) throw new Error(PORTAL_COLLAPSED);
    throw err;
  } finally {
    gate?.cancel();
    iframe.remove();
  }
}

function scripted(abs: string, box: LayoutBox, progress: (msg: string, p: number) => void, watch?: HopWatch): Promise<Level> {
  const url = new URL(abs);
  const src = `/p/${proxyToken()}/${url.protocol.replace(":", "")}/${url.host}${url.pathname}${url.search}`;
  const iframe = document.createElement("iframe");
  iframe.setAttribute("sandbox", "allow-scripts");
  iframe.title = "page";
  iframe.style.cssText = `position:fixed;left:0;top:0;width:${box.layoutW}px;height:${Math.max(480, box.viewH)}px;opacity:0.001;pointer-events:none;border:0;z-index:-10`;
  document.body.appendChild(iframe);
  return new Promise<Level>((resolve, reject) => {
    let alive = false;
    let settled = false;
    let lateAlive = 0;
    const aliveTimer = watch ? 0 : window.setTimeout(() => {
      if (!alive) fail(new Error("The page did not start."));
    }, 15000);
    const jobTimer = watch ? 0 : window.setTimeout(() => fail(new Error("The page took too long.")), 45000);
    const onAbort = () => fail(new Error(PORTAL_COLLAPSED));
    const cleanup = () => {
      window.clearTimeout(aliveTimer);
      window.clearTimeout(jobTimer);
      window.clearTimeout(lateAlive);
      watch?.signal().removeEventListener("abort", onAbort);
      window.removeEventListener("message", onMsg);
      iframe.remove();
    };
    const fail = (err: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err);
    };
    const done = (level: Level) => {
      if (settled) return;
      settled = true;
      cleanup();
      const items = level.paints.length + level.glyphs.length + level.blocks.length;
      if (items < 8) reject(new Error("The page had nothing to break."));
      else resolve(level);
    };
    const onMsg = (ev: MessageEvent) => {
      if (ev.source !== iframe.contentWindow) return;
      const data = ev.data as { type?: string; msg?: string; p?: number; width?: number; level?: Level; message?: string };
      if (!data || typeof data.type !== "string") return;
      if (data.type === "daw:alive") {
        alive = true;
        window.clearTimeout(aliveTimer);
        window.clearTimeout(lateAlive);
        iframe.contentWindow?.postMessage({ type: "daw:extract", opts: options(box, abs), askWidth: true }, "*");
      } else if (data.type === "daw:width") {
        const width = Math.max(box.layoutW, Number(data.width) || box.layoutW);
        iframe.style.width = `${width}px`;
        iframe.contentWindow?.postMessage({ type: "daw:resized", opts: { ...options(box, abs), layoutW: width } }, "*");
      } else if (data.type === "daw:progress") {
        const p = Number(data.p) || 0;
        progress(data.msg || "Running the page", 0.1 + p * 0.75);
      }
      else if (data.type === "daw:level" && data.level) done(data.level);
      else if (data.type === "daw:error") fail(new Error(data.message || "The page failed."));
    };
    window.addEventListener("message", onMsg);
    if (watch) {
      if (watch.failed()) {
        fail(new Error(PORTAL_COLLAPSED));
        return;
      }
      watch.signal().addEventListener("abort", onAbort, { once: true });
      iframe.addEventListener("error", () => fail(new Error("The page did not start.")));
      iframe.addEventListener("load", () => {
        if (settled || alive) return;
        progress("Running the page", 0.12);
        lateAlive = window.setTimeout(() => {
          if (!alive) fail(new Error("The page did not start."));
        }, 1200);
      });
    }
    progress("Running the page", 0.08);
    iframe.src = src;
  });
}


function demoProgress(elapsed: number): { msg: string; p: number } {
  const t = elapsed / 1250;
  const msg = t < 0.22 ? "Fetching the page" : t < 0.48 ? "Laying it out" : t < 0.75 ? "Loading fonts and images" : "Reading the page";
  if (t <= 1) {
    const eased = t * t * (3 - 2 * t);
    return { msg, p: 0.06 + eased * 0.72 };
  }
  return { msg, p: 0.78 + 0.06 * (1 - Math.exp(-(t - 1) * 1.4)) };
}

async function bundledDemo(box: LayoutBox, progress: (msg: string, p: number) => void): Promise<Level> {
  const started = performance.now();
  let running = true;
  const pump = () => {
    if (!running) return;
    const step = demoProgress(performance.now() - started);
    progress(step.msg, step.p);
    requestAnimationFrame(pump);
  };
  pump();
  try {
    const res = await fetch(DEMO_SNAPSHOT);
    if (!res.ok) throw new Error("Demo page is missing.");
    const html = await res.text();
    const iframe = document.createElement("iframe");
    iframe.setAttribute("sandbox", "allow-same-origin");
    iframe.title = "archive.org";
    iframe.style.cssText = `position:fixed;left:0;top:0;width:${box.layoutW}px;height:${Math.max(640, box.viewH)}px;opacity:0;pointer-events:none;border:0;z-index:-1`;
    const loaded = new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error("The page took too long.")), 20000);
      iframe.onload = () => {
        window.clearTimeout(timer);
        resolve();
      };
    });
    iframe.srcdoc = html;
    document.body.appendChild(iframe);
    try {
      await loaded;
      const doc = iframe.contentDocument;
      const view = iframe.contentWindow;
      if (!doc || !view) throw new Error("Could not read the page.");
      if (doc.fonts?.ready) await Promise.race([doc.fonts.ready, wait(1500)]);
      const pending = [...doc.images].map((img) => (img.complete ? null : new Promise<void>((resolve) => {
        img.addEventListener("load", () => resolve(), { once: true });
        img.addEventListener("error", () => resolve(), { once: true });
      })));
      await Promise.race([Promise.all(pending), wait(2500)]);
      const level = await extractLevel(doc, view, options(box, DEMO_URL), () => {});
      const items = level.paints.length + level.glyphs.length + level.blocks.length;
      if (items < 8) throw new Error("The page had nothing to break.");
      level.url = DEMO_URL;
      level.title = level.title || "archive.org";
      const remain = 1250 - (performance.now() - started);
      if (remain > 0) await wait(remain);
      return level;
    } finally {
      iframe.remove();
    }
  } finally {
    running = false;
  }
}

function safeHost(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
}

function whenAborted(watch: HopWatch): Promise<never> {
  return new Promise((_, reject) => {
    if (watch.failed()) reject(new Error(PORTAL_COLLAPSED));
    else watch.signal().addEventListener("abort", () => reject(new Error(PORTAL_COLLAPSED)), { once: true });
  });
}

export async function loadLevel(raw: string, box: LayoutBox, progress: (msg: string, p: number) => void, live = false, watch?: HopWatch): Promise<Level> {
  const spec = classify(raw);
  if (spec.kind === "map") {
    progress("Laying it out", 0.4);
    return fetchLevel(spec.url);
  }
  // Journey's later pages always use the proxy, even for the archive.org homepage.
  if (spec.kind === "demo" && !live) return bundledDemo(box, progress);
  if (riskyTarget(spec.url)) throw new Error(RISK_MESSAGE);
  // The live archive.org copy needs only the session token; any other site needs the person check.
  const archive = /^(www\.)?archive\.org$/i.test(safeHost(spec.url));
  if (watch) {
    await Promise.race([archive ? ensureToken(false, 180_000) : ensureHuman(), whenAborted(watch)]).catch(() => {
      throw new Error(PORTAL_COLLAPSED);
    });
  } else if (archive) await ensureToken(false, 180_000);
  else await ensureHuman();
  let first: unknown;
  const tries = watch ? 1 : 2;
  for (let attempt = 0; attempt < tries; attempt++) {
    if (watch?.capped()) throw new Error(PORTAL_COLLAPSED);
    const began = performance.now();
    try {
      return await scripted(spec.url, box, progress, watch);
    } catch (err) {
      if (!watch) {
        first = err;
        if (performance.now() - began > 20000) break;
        continue;
      }
      // A blocked or aborted hop collapses now. A stall before the page really
      // started still gets one static try on a fresh 8s window, under the same cap.
      if (blockedRequest(err) || watch.capped()) throw new Error(PORTAL_COLLAPSED);
      if (watch.failed() && (watch.peak() >= 0.18 || !watch.rearm())) throw new Error(PORTAL_COLLAPSED);
      first = err;
    }
  }
  if (watch?.capped() || watch?.failed()) throw new Error(PORTAL_COLLAPSED);
  progress("Reading a static copy", 0.5);
  try {
    return await staticPage(spec.url, box, progress, watch);
  } catch (fallback) {
    if (watch) throw new Error(PORTAL_COLLAPSED);
    const message = fallback instanceof Error ? fallback.message : "The site could not be reached.";
    const prior = first instanceof Error ? first.message : message;
    throw new Error(message || prior);
  }
}

const MAP_FACES: Array<[string, string, string, string]> = [
  ["Impact", "/assets/maps/fonts/Anton-Regular.ttf", "100 900", "normal"],
  ["Arial Black", "/assets/maps/fonts/ArchivoBlack-Regular.ttf", "100 900", "normal"],
  ["Arial", "/assets/maps/fonts/Arimo-Regular.ttf", "400", "normal"],
  ["Arial", "/assets/maps/fonts/Arimo-Bold.ttf", "700", "normal"],
  ["Arial", "/assets/maps/fonts/Arimo-Italic.ttf", "400 700", "italic"],
  ["Courier New", "/assets/maps/fonts/Cousine-Regular.ttf", "400", "normal"],
  ["Courier New", "/assets/maps/fonts/Cousine-Bold.ttf", "700", "normal"],
  ["Georgia", "/assets/maps/fonts/Gelasio-wght.ttf", "400 700", "normal"],
];

function needsMapFonts(level: Level): boolean {
  return level.glyphs.some((glyph) => /impact|arial black|anton|archivo/i.test(glyph.font));
}

async function ensureFaces(): Promise<void> {
  if (!document.fonts) return;
  const ready = MAP_FACES.every(([family, , weight, style]) => document.fonts.check(`${style} ${weight} 16px "${family}"`));
  if (ready) return;
  await Promise.race([
    Promise.all(MAP_FACES.map(async ([family, url, weight, style]) => {
      try {
        const face = new FontFace(family, `url(${url})`, { weight, style, display: "block" });
        await face.load();
        document.fonts.add(face);
      } catch {
        /* a missing face falls back to the generic family */
      }
    })),
    wait(400),
  ]);
}

export async function prepareLevel(level: Level): Promise<void> {
  let style = document.getElementById("daw-fonts") as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = "daw-fonts";
    document.head.appendChild(style);
  }
  style.textContent = `${needsMapFonts(level) ? MAP_FONT_CSS + "\n" : ""}${level.fontFaces.join("\n")}`;
  const listed = (level.fonts ?? []).slice(0, 60).map((spec) => document.fonts?.load(spec).catch(() => []) ?? Promise.resolve([]));
  const fonts = needsMapFonts(level) ? ensureFaces() : Promise.race([Promise.all(listed), wait(5000)]);
  await Promise.all([
    fonts,
    Promise.all(level.paints.map((paint) => (paint.k === "image" ? decodePaint(paint) : undefined))),
  ]);
}

function loadImage(url: string, ms: number): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    if (!url.startsWith("data:") && !url.startsWith("blob:")) img.crossOrigin = "anonymous";
    const timer = window.setTimeout(() => resolve(null), ms);
    img.onload = () => {
      window.clearTimeout(timer);
      resolve(img.naturalWidth > 0 ? img : null);
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      resolve(null);
    };
    img.src = url;
  });
}

/** Turn a paint URL into a bitmap. SVG data URLs are rasterized so the page canvas can draw them. */
async function decodePaint(paint: ImgPaint): Promise<void> {
  if (typeof paint.src !== "string" || !paint.src) return;
  const url = paint.src;
  const img = await loadImage(url, url.startsWith("data:") ? 800 : 1200);
  if (!img) return;
  (paint as ImgPaint & { url?: string }).url = url;
  if (url.startsWith("data:image/svg")) {
    const scale = 2;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.ceil((paint.dw || img.naturalWidth) * scale));
    canvas.height = Math.max(1, Math.ceil((paint.dh || img.naturalHeight) * scale));
    const g = canvas.getContext("2d");
    if (g) {
      g.drawImage(img, 0, 0, canvas.width, canvas.height);
      paint.src = canvas;
      return;
    }
  }
  paint.src = img;
}

export function serializable(level: Level): Level {
  return {
    ...level,
    links: undefined,
    paints: level.paints.map((paint) => {
      if (paint.k !== "image" || typeof paint.src === "string") return paint;
      const url = (paint as ImgPaint & { url?: string }).url ?? "";
      return { ...paint, src: url };
    }),
  };
}

export async function gzipBody(value: unknown): Promise<Blob> {
  const json = JSON.stringify(value);
  if (typeof CompressionStream === "undefined") return new Blob([json], { type: "application/json" });
  const stream = new Blob([json]).stream().pipeThrough(new CompressionStream("gzip"));
  return new Response(stream).blob();
}
